import type {
  CargoCategory,
  ErrorBody,
  ErrorCode,
  OrderStatus,
  ServiceType,
  TakeoffApplicationStatus,
} from '@sea-drone/shared'

/**
 * 监管端 HTTP 客户端。
 *
 * 与 apps/admin-ops 的客户端同构,三处**刻意的不同**:
 *
 *   ① 令牌存另一个键(sea-drone.gov.token)—— 同一个浏览器里两个端各登各的,
 *      共用键会让后登的那个把先登的挤掉,表现是「运营端莫名其妙掉线」
 *   ② 登录打 /api/gov/auth/login,身份是 AdminIdentity(kind=gov)
 *   ③ 类型里**没有金额字段** —— GovOrderItem / GovOrderDetail 就是契约里那一组。
 *      这里手写一份「反正不显示」的金额字段等于把那条纪律挪进页面,
 *      而页面是最容易某天顺手多显示一列的地方
 *
 * 非 2xx 一律抛 ApiError,绝不返回 null 或空数组 —— 返回空数组会让
 * 「请求失败」和「一条数据都没有」在页面上长得一模一样(与运营端同一条理由)。
 */

export class ApiError extends Error {
  readonly code: ErrorCode | 'HTTP_TRANSPORT'
  readonly status: number
  readonly requestId: string
  readonly details?: Record<string, unknown>

  constructor(opts: {
    code: ErrorCode | 'HTTP_TRANSPORT'
    message: string
    status: number
    requestId: string
    details?: Record<string, unknown>
  }) {
    super(opts.message)
    this.name = 'ApiError'
    this.code = opts.code
    this.status = opts.status
    this.requestId = opts.requestId
    this.details = opts.details
  }
}

const TOKEN_KEY = 'sea-drone.gov.token'

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ''
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

export function hasToken(): boolean {
  return getToken() !== ''
}

/**
 * 401 把人送回登录页(令牌过期 12 小时,或被运营端停用 —— 停用下一次请求就生效)。
 * 403 不跳登录页:那是「这个令牌不该碰这个路径」,而监管端页面上根本不该发出这种请求;
 * 真出现了就把它显示出来,那是一个该被看见的信号。
 */
function redirectToLogin(): void {
  clearToken()
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.assign('/login')
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${getToken()}`,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (e) {
    throw new ApiError({
      code: 'HTTP_TRANSPORT',
      message: `连不上 api 服务(${method} ${path}):${e instanceof Error ? e.message : String(e)}。先确认 api 起着(端口 3000)`,
      status: 0,
      requestId: 'no-request-id',
    })
  }

  const text = await res.text()

  if (!res.ok) {
    const parsed = parseErrorBody(text)
    if (res.status === 401) redirectToLogin()
    throw new ApiError({
      code: parsed?.code ?? 'HTTP_TRANSPORT',
      message: parsed?.message ?? `${method} ${path} 返回 ${res.status},响应体不是契约里的 Error 结构:${text.slice(0, 200)}`,
      status: res.status,
      requestId: parsed?.requestId ?? res.headers.get('x-request-id') ?? 'no-request-id',
      ...(parsed?.details ? { details: parsed.details } : {}),
    })
  }

  if (text.length === 0) return undefined as T
  return JSON.parse(text) as T
}

function parseErrorBody(text: string): ErrorBody | null {
  let json: unknown
  try {
    json = JSON.parse(text)
  } catch {
    /* @intentional-swallow(reason="这里的失败本身就是返回值:响应体不是 JSON。调用方拿到 null 之后会把原始文本连同状态码一起抛出去,信息不会丢") */
    return null
  }
  if (json && typeof json === 'object' && typeof (json as ErrorBody).code === 'string') {
    return json as ErrorBody
  }
  return null
}

export interface Page<T> {
  items: T[]
  page: number
  pageSize: number
  total: number
}

/** 契约 components.schemas.AdminIdentity —— 监管端登录返回体里的那一半 */
export interface AdminIdentity {
  userId: string
  username: string
  kind: 'ops' | 'gov'
}

export interface AdminLoginResult {
  token: string
  expiresAt: string
  identity: AdminIdentity
}

/** 契约 components.schemas.GovOrderItem —— **没有任何金额字段** */
export interface GovOrderItem {
  orderNo: string
  status: OrderStatus
  serviceType: ServiceType
  cargoCategory: CargoCategory
  originArea: string | null
  destinationArea: string | null
  carrierName: string | null
  createdAt: string
}

/** 契约 components.schemas.Proof —— 短时效签名 URL,不是公开直链(Y-02) */
export interface Proof {
  node: string
  fileKey: string
  url: string
  urlExpiresAt: string
  uploadedAt: string
  takenAt: string | null
  supersededCount: number
}

/** 契约 components.schemas.GovOrderDetail */
export interface GovOrderDetail extends GovOrderItem {
  cargoDescription: string | null
  pilotName: string | null
  proofs: Proof[]
  /** 恒为 false(契约里是 enum: [false])—— 视频本期整块不做,这个空位是显式的 */
  videoPlaceholder: boolean
}

/** 契约 components.schemas.GovDashboard */
export interface GovDashboard {
  orderCount: number
  flightCount: number
  inTransitCount: number
  byArea: { area: string; count: number }[]
}

/** 契约 components.schemas.GeoPoint —— 起终点的经纬度 + 地址 + 区域名 */
export interface GeoPoint {
  lat: number
  lng: number
  address: string | null
  area: string | null
}

/**
 * 契约 components.schemas.TakeoffApplication —— **不含任何金额字段**(L0-01)。
 *
 * 起终点与货物是**提交时刻的快照**(Y-37):监管看到的必须是「申报当时说了什么」,
 * 而不是「订单后来变成了什么」—— 所以这里读的就是申请行自己的值,不去 join 订单。
 */
export interface TakeoffApplication {
  id: string
  orderNo: string
  status: TakeoffApplicationStatus
  aircraftModel: string
  plannedTakeoffAt: string
  origin: GeoPoint
  destination: GeoPoint
  cargoCategory: CargoCategory
  cargoDescription: string | null
  weightGram: number
  oversized: boolean
  volumeCm3: number | null
  pilotName: string | null
  carrierName: string | null
  reviewNote: string | null
  reviewedAt: string | null
  reviewedBy: string | null
  submittedAt: string
}

/** 契约 components.schemas.ReviewTakeoffApplicationInput —— 驳回必须填意见(AS-100) */
export interface ReviewTakeoffApplicationInput {
  approve: boolean
  note?: string
}

export const api = {
  /** 监管端登录。**这是唯一一个不带令牌的请求** */
  login: (username: string, password: string) =>
    request<AdminLoginResult>('POST', '/api/gov/auth/login', { username, password }),

  listOrders: (q: { page?: number; pageSize?: number } = {}) => {
    const params = new URLSearchParams({ page: String(q.page ?? 1), pageSize: String(q.pageSize ?? 50) })
    return request<Page<GovOrderItem>>('GET', `/api/gov/orders?${params.toString()}`)
  },

  getOrder: (orderNo: string) => request<GovOrderDetail>('GET', `/api/gov/orders/${encodeURIComponent(orderNo)}`),

  dashboard: (q: { from?: string; to?: string } = {}) => {
    const params = new URLSearchParams()
    if (q.from) params.set('from', q.from)
    if (q.to) params.set('to', q.to)
    const qs = params.toString()
    return request<GovDashboard>('GET', `/api/gov/dashboard${qs ? `?${qs}` : ''}`)
  },

  /** 起飞申请列表(S-53)。按提交时间倒序;按状态、机型(模糊)、订单号筛选;分页 */
  listTakeoffApplications: (
    q: { page?: number; pageSize?: number; status?: TakeoffApplicationStatus; aircraftModel?: string; orderNo?: string } = {},
  ) => {
    const params = new URLSearchParams({ page: String(q.page ?? 1), pageSize: String(q.pageSize ?? 50) })
    if (q.status) params.set('status', q.status)
    if (q.aircraftModel) params.set('aircraftModel', q.aircraftModel)
    if (q.orderNo) params.set('orderNo', q.orderNo)
    return request<Page<TakeoffApplication>>('GET', `/api/gov/takeoff-applications?${params.toString()}`)
  },

  getTakeoffApplication: (id: string) =>
    request<TakeoffApplication>('GET', `/api/gov/takeoff-applications/${encodeURIComponent(id)}`),

  /**
   * 审核起飞申请 —— **全系统唯一一条监管端写路由**(Y-36)。
   *
   * 它是这个只读端上唯一一处写得动的东西,而且判据钉死到「POST + 这个路径」这一个形状:
   * guards/check-ui-design-system.mjs 的写调用白名单里只有这一条,
   * guards/check-gov-readonly.mjs 的服务端白名单里也只有它(外加登录那一条)。
   * 在这里加第二个写方法、或在视图里调第二个写方法,两条守卫都会红。
   */
  reviewTakeoffApplication: (id: string, body: ReviewTakeoffApplicationInput) =>
    request<TakeoffApplication>('POST', `/api/gov/takeoff-applications/${encodeURIComponent(id)}/review`, body),
}
